/**
 * Quantised player input. Everything is an integer so the same bytes mean the
 * same thing on every machine; the simulation converts at the point of use.
 *
 * Buttons are actions, not keys: the simulation must not know which key a
 * player bound to an action. `apps/client/src/input/controls.ts` owns the map.
 */
export const Buttons = {
  ARM_L: 1 << 0,
  ARM_R: 1 << 1,
  LEG_L: 1 << 2,
  LEG_R: 1 << 3,
  GUARD: 1 << 4,
  STEP: 1 << 5,
  DUCK: 1 << 6,
  /** Arc modifiers held while a limb button is pressed: around (hook), rising (uppercut). */
  ARC_AROUND: 1 << 7,
  ARC_RISING: 1 << 8,
  /** Slip the upper body sideways off the aim line. */
  LEAN_L: 1 << 9,
  LEAN_R: 1 << 10,
  /** Switch stance (orthodox ↔ southpaw); edge-triggered. */
  STANCE: 1 << 11,
  /**
   * Target modifiers, held at a limb press or while covering (design §5.1,
   * §6): ALT is a limb's other upper target (a punch to the body, a kick to
   * the head; a cover over the body), LOW goes for the legs (a kick to the
   * legs, a cover that checks; a punch goes no lower than the body).
   */
  AIM_ALT: 1 << 12,
  AIM_LOW: 1 << 13,
} as const;
export type Button = (typeof Buttons)[keyof typeof Buttons];

export interface PlayerInput {
  /** Simulation tick this input is for. */
  tick: number;
  /** Movement axes, -127..127 (right / forward relative to look yaw). */
  moveX: number;
  moveY: number;
  /** Look yaw, 0..65535 maps to 0..2π. */
  yaw: number;
  /** Look pitch in degrees, -89..89 (positive looks up). */
  pitch: number;
  /** Bitfield of Buttons. */
  buttons: number;
  /**
   * Server tick of the world this player was looking at when they made this
   * input (remote fighters are drawn in the past), or 0 for "the present".
   * Strikes are resolved against opponents' positions at that tick, within
   * `tuning.lagCompensation.maxRewindTicks` (lag compensation).
   */
  viewTick: number;
}

export const YAW_STEPS = 65536;

export function emptyInput(tick: number): PlayerInput {
  return { tick, moveX: 0, moveY: 0, yaw: 0, pitch: 0, buttons: 0, viewTick: 0 };
}

export function has(input: PlayerInput, button: Button): boolean {
  return (input.buttons & button) !== 0;
}

export function clampInput(input: PlayerInput): PlayerInput {
  return {
    tick: input.tick >>> 0,
    moveX: clampInt(input.moveX, -127, 127),
    moveY: clampInt(input.moveY, -127, 127),
    yaw: ((input.yaw % YAW_STEPS) + YAW_STEPS) % YAW_STEPS,
    pitch: clampInt(input.pitch, -89, 89),
    buttons: input.buttons & 0xffff,
    viewTick: (input.viewTick ?? 0) >>> 0,
  };
}

function clampInt(value: number, min: number, max: number): number {
  const v = Math.trunc(value);
  return v < min ? min : v > max ? max : v;
}

/** Radians → yaw steps, for clients and bots. */
export function yawFromRadians(radians: number): number {
  const turns = radians / (2 * Math.PI);
  const steps = Math.round((turns - Math.floor(turns)) * YAW_STEPS);
  return steps % YAW_STEPS;
}
